import { Heart, Gift, Sprout, Backpack, Users, Coffee, Package, MapPin, Truck, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function HeaderHoliday() {
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [productType, setProductType] = useState<'giftBox' | 'singleBag' | null>(null);
  const isSoldOut = true;

  const scrollToDetails = () => {
    const detailsSection = document.getElementById('details-section');
    detailsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleOrderClick = () => {
    if (isSoldOut) return;
    setShowOrderModal(true);
    setProductType(null);
  };

  const handleCloseModal = () => {
    setShowOrderModal(false);
    setProductType(null);
  };

  return (
    <div className="min-h-screen bg-[#132947]">
      <Header />
      {/* Hero Section */}
      <div className="relative min-h-screen overflow-hidden">
        {/* Background with parallax effect */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1513569771920-c9e1d31714af?w=1920&q=80)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#132947]/80 via-[#132947]/90 to-[#132947]" />
        </div>

        {/* Enhanced decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#fda36b]/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#fda36b]/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-[#fda36b]/5 to-transparent rounded-full blur-2xl" />

        {/* Floating snowflake decorations */}
        <div className="absolute top-32 left-1/4 text-[#fda36b]/20 text-4xl">❄</div>
        <div className="absolute top-48 right-1/4 text-[#fda36b]/30 text-3xl">❄</div>
        <div className="absolute bottom-40 left-1/3 text-[#fda36b]/20 text-5xl">❄</div>

        <div className="relative min-h-screen flex items-center justify-center px-6 max-w-7xl mx-auto py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center w-full">
            {/* Left side - Content */}
            <div className="text-center lg:text-left space-y-6">
              {/* Main heading with refined typography and decorative line */}
              <div className="relative">
                <div className="absolute -left-4 top-8 w-1 h-32 bg-gradient-to-b from-[#fda36b] to-transparent rounded-full hidden lg:block" />
                <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-start gap-6 lg:gap-8">
                  <h1 className="text-6xl md:text-8xl font-bold text-white mb-4 tracking-tight leading-none">
                    Header
                    <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#fda36b] to-[#fda36b]/70 mt-2">Holiday</span>
                  </h1>
                  <img
                    src="/Logo Transparent.png"
                    alt="SPUR Logo"
                    className="w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80 xl:w-96 xl:h-96 object-contain flex-shrink-0"
                  />
                </div>
              </div>

              {/* Subtitle with better spacing and visual separator */}
              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#fda36b]/50" />
                  <span className="text-[#fda36b]/70 text-sm font-semibold uppercase tracking-wider">Limited Edition</span>
                  <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#fda36b]/50" />
                </div>

                <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                  A limited edition holiday blend crafted in partnership with{' '}
                  <a href="https://www.spurnorthshore.org/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#fda36b] transition-colors underline decoration-white/30 hover:decoration-[#fda36b] font-semibold">SPUR</a>
                </p>

                <p className="text-lg md:text-xl text-white/70 font-light">
                  Exceptional coffee that supports families in our community
                </p>
              </div>

              {/* CTA buttons with improved styling */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                <button
                  onClick={handleOrderClick}
                  disabled={isSoldOut}
                  className={`group relative px-12 py-6 rounded-xl font-bold text-xl transition-all duration-300 inline-flex items-center justify-center gap-3 ${
                    isSoldOut
                      ? 'bg-white/10 text-white/50 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#fda36b] to-[#fda36b]/90 text-[#132947] hover:from-[#fda36b]/90 hover:to-[#fda36b]/80 shadow-2xl shadow-[#fda36b]/40 transform hover:scale-105 hover:shadow-[#fda36b]/60'
                  } overflow-hidden`}
                >
                  {!isSoldOut && <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />}
                  <Gift className={`w-6 h-6 ${!isSoldOut && 'group-hover:rotate-12'} transition-transform`} />
                  {isSoldOut ? 'Sold Out' : 'Order Now'}
                </button>
                <button
                  onClick={scrollToDetails}
                  className="bg-white/5 text-white border-2 border-white/20 px-10 py-6 rounded-xl font-semibold text-lg hover:bg-white/10 hover:border-[#fda36b]/50 transition-all duration-300 backdrop-blur-md"
                >
                  Learn More
                </button>
              </div>
            </div>

            {/* Right side - Product image with enhanced styling */}
            <div className="flex items-center justify-center lg:justify-end relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#fda36b]/20 to-transparent rounded-3xl blur-3xl scale-110" />
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#fda36b]/30 to-transparent rounded-3xl blur-xl" />
                <img
                  src="/Generated Image November 05, 2025 - 9_50AM.png"
                  alt="Header Holiday Gift Package"
                  className="relative w-full max-w-md rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500 border-2 border-white/10"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Partnership Section */}
      <div id="details-section" className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Coffee That Makes a Difference
          </h2>
          <div className="w-20 h-1 bg-[#fda36b] mx-auto rounded-full" />
        </div>

        <div className="bg-gradient-to-br from-[#fda36b]/10 to-transparent border border-[#fda36b]/20 rounded-2xl p-8 md:p-12">
          <div className="flex items-center justify-center gap-4 mb-8">
            <Coffee className="w-8 h-8 text-[#fda36b]" />
            <span className="text-2xl text-white font-bold">+</span>
            <Users className="w-8 h-8 text-[#fda36b]" />
          </div>

          <h3 className="text-2xl md:text-3xl font-bold text-white text-center mb-6">
            Partnering with <a href="https://www.spurnorthshore.org/" target="_blank" rel="noopener noreferrer" className="text-[#fda36b] hover:text-[#fda36b]/80 transition-colors underline decoration-[#fda36b]/30 hover:decoration-[#fda36b]">SPUR</a>
          </h3>

          <p className="text-white/80 text-lg leading-relaxed text-center max-w-2xl mx-auto mb-6">
            When you gift Header Holiday, you're not just giving exceptional coffee — you're supporting{' '}
            <a
              href="https://www.spurnorthshore.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#fda36b] hover:text-[#fda36b]/80 transition-colors underline decoration-[#fda36b]/30 hover:decoration-[#fda36b] font-semibold"
            >
              SPUR
            </a>, a local organization that brings joy, nourishment, and essential resources to families in our community.
          </p>

          <p className="text-white/90 text-lg leading-relaxed text-center max-w-2xl mx-auto mb-8">
            From filling backpacks and planting gardens to ensuring every child has a gift to open this holiday season, your purchase helps make it happen. It's a gift that truly keeps on giving.
          </p>

          <div className="text-center">
            <button
              onClick={handleOrderClick}
              className="bg-[#fda36b] text-[#132947] px-10 py-5 rounded-lg font-bold text-xl hover:bg-[#fda36b]/90 transition-all duration-300 inline-flex items-center gap-3 shadow-xl shadow-[#fda36b]/30 transform hover:scale-105"
            >
              <Heart className="w-6 h-6" fill="currentColor" />
              Order Now & Support <span className="hover:underline">SPUR</span>
            </button>
          </div>
        </div>
      </div>

      {/* The Story - Marblehead Magic */}
      <div className="max-w-6xl mx-auto px-6 pb-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            You're Home Now
          </h2>
          <div className="w-20 h-1 bg-[#fda36b] mx-auto rounded-full" />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Label Image */}
          <div className="flex items-center justify-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#fda36b]/20 to-transparent rounded-2xl blur-xl" />
              <img
                src="/Header Holiday Bag Label (1) copy.png"
                alt="Header Holiday Label"
                className="relative w-full rounded-2xl shadow-2xl border-2 border-white/10"
              />
            </div>
          </div>

          {/* Story Text */}
          <div className="prose prose-lg prose-invert">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12 space-y-5 text-white/90 leading-relaxed text-lg">
              <p>
                Every town has its December heartbeat — the lights, the laughter, the smell of coffee and cold air. Here in Marblehead, it lives in the breakfast lines at The Driftwood, Mookie's, and The Muffin Shop. In the bells of Abbot Hall, the wall-to-wall noise at Maddie's, and the glow of Old Town's lights reflecting off the harbor. You can feel it when you bump into someone who still remembers your old 631 number, or when your kids run down the same streets you once did, chasing the same kind of magic.
              </p>

              <p>
                It's a roast for mornings that smell like muffins and salt air, and nights that make you slow down just long enough to realize how lucky you are — to be here, now, holding the hands you know you won't always get to hold.
              </p>

              <p>
                Because home isn't about how long you've been here. It's about belonging — to the laughter, the light, and the people who make the world feel small in the best possible way. Because we wish everyone could feel as lucky as we do to call this place home — where our friends are our family, our family are our friends, and the lines between the two blur in the best possible way.
              </p>

              <p>
                It's about giving back to the place that gives so much, and knowing that whoever you are, wherever you're from, your community is here for you.
              </p>

              <p className="text-[#fda36b] font-semibold">
                So feel the love. Pour a cup. Let it steam the windows. Smile at the sound of the bells.
              </p>

              <p className="text-xl font-bold">
                You're home now.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Impact Areas */}
      <div className="bg-gradient-to-b from-transparent to-white/5 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Your Coffee Makes a Difference
            </h2>
            <p className="text-white/70 text-lg">
              Every bag of Header Holiday helps <a href="https://www.spurnorthshore.org/" target="_blank" rel="noopener noreferrer" className="text-[#fda36b] hover:text-[#fda36b]/80 transition-colors underline decoration-[#fda36b]/30 hover:decoration-[#fda36b]">SPUR</a> support our community
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Backpack,
                title: 'Filling Backpacks',
                description: 'Ensuring students have the supplies they need to succeed'
              },
              {
                icon: Sprout,
                title: 'Planting Gardens',
                description: 'Growing fresh food and teaching sustainable practices'
              },
              {
                icon: Gift,
                title: 'Holiday Joy',
                description: 'Making sure every child has a gift to open this season'
              }
            ].map((impact, index) => (
              <div
                key={index}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center hover:bg-white/10 transition-all duration-300 group"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#fda36b]/20 rounded-full mb-6 group-hover:scale-110 transition-transform">
                  <impact.icon className="w-8 h-8 text-[#fda36b]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {impact.title}
                </h3>
                <p className="text-white/70 leading-relaxed">
                  {impact.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* The Gift Box */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            The Perfect Gift
          </h2>
          <div className="w-20 h-1 bg-[#fda36b] mx-auto rounded-full" />
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
          {/* Gift Box Image */}
          <div className="rounded-2xl overflow-hidden">
            <img
              src="/Generated Image November 05, 2025 - 9_50AM.png"
              alt="Header Holiday Gift Package"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Gift Box Contents */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-white mb-6">
              Each Gift Box Includes
            </h3>

            <div className="space-y-4">
              <div className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#fda36b]/20 rounded-full flex items-center justify-center">
                  <Coffee className="w-5 h-5 text-[#fda36b]" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">12 oz Bag of Header Holiday</h4>
                  <p className="text-white/70 text-sm">Our special limited edition roast</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#fda36b]/20 rounded-full flex items-center justify-center">
                  <Gift className="w-5 h-5 text-[#fda36b]" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Commemorative Bamboo Coffee Scoop</h4>
                  <p className="text-white/70 text-sm">A keepsake from this special blend</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#fda36b]/20 rounded-full flex items-center justify-center">
                  <Heart className="w-5 h-5 text-[#fda36b]" fill="currentColor" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">The Gift of Giving Back</h4>
                  <p className="text-white/70 text-sm">The feeling of knowing you're helping your community</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#fda36b]/10 to-transparent border border-[#fda36b]/20 rounded-xl p-6 mt-6">
              <p className="text-white/90 leading-relaxed">
                <span className="font-semibold text-[#fda36b]">Ready to gift:</span> Each box is beautifully packaged and includes an 8.5" × 5.5" postcard explaining the mission and story of the roast.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* The Blend */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            The Blend
          </h2>
          <div className="w-20 h-1 bg-[#fda36b] mx-auto rounded-full" />
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gradient-to-br from-[#fda36b]/10 to-transparent border border-[#fda36b]/20 rounded-2xl p-8 text-center">
            <div className="text-4xl font-bold text-[#fda36b] mb-2">40%</div>
            <h3 className="text-xl font-semibold text-white mb-3">Ethiopian Guji</h3>
            <p className="text-white/60 text-sm mb-3">Kayon Mountain, Washed Gr. 1</p>
            <div className="text-white/80 text-sm space-y-1">
              <p className="font-medium text-[#fda36b]">Notes:</p>
              <p>Peach, Lemon, Chocolate, Floral, Spice</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#fda36b]/10 to-transparent border border-[#fda36b]/20 rounded-2xl p-8 text-center">
            <div className="text-4xl font-bold text-[#fda36b] mb-2">40%</div>
            <h3 className="text-xl font-semibold text-white mb-3">Ethiopian Yirgacheffe</h3>
            <p className="text-white/60 text-sm mb-3">Idedo Natural Gr. 1</p>
            <div className="text-white/80 text-sm space-y-1">
              <p className="font-medium text-[#fda36b]">Notes:</p>
              <p>Dark Fruit, Tea, Chocolate, Jasmine, Spice</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#fda36b]/10 to-transparent border border-[#fda36b]/20 rounded-2xl p-8 text-center">
            <div className="text-4xl font-bold text-[#fda36b] mb-2">20%</div>
            <h3 className="text-xl font-semibold text-white mb-3">Brazil Mogiana</h3>
            <p className="text-white/60 text-sm mb-3">Guaxupé FC SS</p>
            <div className="text-white/80 text-sm space-y-1">
              <p className="font-medium text-[#fda36b]">Notes:</p>
              <p>Chocolate, Caramel, Almond, Smooth</p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12 space-y-6">
          <h3 className="text-2xl font-bold text-white mb-4">Tasting Profile</h3>
          <p className="text-white/80 leading-relaxed">
            Header Holiday is a harmonious blend that captures the spirit of the season. The two Ethiopian coffees bring bright, complex fruit and floral notes — think peach, dark berries, and jasmine — balanced by gentle tea-like and chocolate undertones. The Brazilian base adds a smooth, rich foundation with caramel sweetness and nutty notes that tie everything together.
          </p>
          <p className="text-white/80 leading-relaxed">
            This medium roast delivers a clean, sweet cup with layered complexity: crisp citrus and stone fruit up front, a silky chocolate body, and a smooth, lingering finish. Perfect for pour-over, drip, or French press — and absolutely delightful as an afternoon espresso.
          </p>
          <div className="grid md:grid-cols-2 gap-4 pt-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-2">Roast Level</h4>
              <p className="text-white/70">Medium</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-2">Best Brewing Methods</h4>
              <p className="text-white/70">Pour-Over, Drip, French Press, Espresso</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing & Availability */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Order Today
          </h2>
          <p className="text-white/70 text-xl">Limited edition — once they're gone, they're gone</p>
        </div>

        <div className="bg-gradient-to-br from-[#fda36b]/10 to-transparent border-2 border-[#fda36b]/40 rounded-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-baseline gap-2 mb-2">
              <span className="text-5xl md:text-6xl font-bold text-white">$42.50</span>
              <span className="text-white/70 text-lg">per gift box</span>
            </div>
            <p className="text-white/60 text-sm mb-4">
              (The latitude of Marblehead)
            </p>
            <p className="text-[#fda36b] font-semibold text-lg">
              Beautifully packaged and gift-ready
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <Gift className="w-8 h-8 text-[#fda36b] mx-auto mb-3" />
              <h4 className="font-semibold text-white mb-2">Deliveries Begin</h4>
              <p className="text-white/70">December 1, 2025</p>
              <p className="text-white/50 text-xs mt-1">Perfect timing for the holidays!</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
              <MapPin className="w-8 h-8 text-[#fda36b] mx-auto mb-3" />
              <h4 className="font-semibold text-white mb-2">Pickup at <a href="https://www.spurnorthshore.org/" target="_blank" rel="noopener noreferrer" className="text-[#fda36b] hover:text-[#fda36b]/80 transition-colors underline decoration-[#fda36b]/30 hover:decoration-[#fda36b]">SPUR</a></h4>
              <p className="text-white/70">or delivered to you</p>
            </div>
          </div>

          <div className="space-y-3 mb-8">
            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#fda36b]" />
                <span className="text-white font-medium">Marblehead Delivery & Pickup</span>
              </div>
              <span className="text-[#fda36b] font-semibold text-lg">FREE</span>
            </div>

            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-[#fda36b]" />
                <span className="text-white font-medium">US Shipping</span>
              </div>
              <span className="text-white/90 font-semibold text-lg">$10</span>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={handleOrderClick}
              disabled={isSoldOut}
              className={`px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 inline-flex items-center gap-3 ${
                isSoldOut
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-[#fda36b] text-[#132947] hover:bg-[#fda36b]/90 shadow-xl shadow-[#fda36b]/30 hover:scale-105 transform'
              }`}
            >
              <Gift className="w-6 h-6" />
              {isSoldOut ? 'Sold Out' : 'Order Your Gift Boxes Now'}
            </button>
            <p className="text-white/60 text-sm mt-4">
              {isSoldOut ? 'Thank you for your support!' : 'Free local delivery • Ships anywhere in the US • Gift-ready packaging included'}
            </p>
          </div>
        </div>
      </div>

      {/* Social Proof / Testimonial */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-8 md:p-12 text-center">
          <div className="flex justify-center mb-6">
            {[...Array(5)].map((_, i) => (
              <Heart key={i} className="w-6 h-6 text-[#fda36b] mx-1" fill="currentColor" />
            ))}
          </div>
          <blockquote className="text-xl md:text-2xl text-white/90 italic mb-6">
            "This is the most meaningful gift I've given in years. The coffee is incredible, and knowing it supports local families makes every cup taste even better."
          </blockquote>
          <p className="text-white/60">— Sarah M., North Shore Resident</p>
        </div>
      </div>

      {/* Final CTA */}
      <div className="border-t border-white/10 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Don't Miss Out on This Limited Edition
          </h3>
          <p className="text-white/70 text-xl mb-8">
            Give exceptional coffee. Support your community. Create lasting memories.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={handleOrderClick}
              disabled={isSoldOut}
              className={`px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 inline-flex items-center justify-center gap-3 ${
                isSoldOut
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-[#fda36b] text-[#132947] hover:bg-[#fda36b]/90 shadow-xl shadow-[#fda36b]/30'
              }`}
            >
              <Gift className="w-6 h-6" />
              {isSoldOut ? 'Sold Out' : 'Order Header Holiday'}
            </button>
            <Link
              to="/"
              className="bg-white/10 text-white border border-white/20 px-8 py-5 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 inline-flex items-center justify-center"
            >
              Explore Bond Coffee
            </Link>
          </div>
          <p className="text-white/50 text-sm">
            Questions? Contact us at hello@bondcoffee.com
          </p>
        </div>
      </div>

      <Footer />

      {/* Order Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#132947] border-2 border-[#fda36b]/30 rounded-2xl max-w-2xl w-full p-8 relative shadow-2xl my-8 max-h-[90vh] overflow-y-auto">
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-10 bg-[#132947]/80 rounded-full p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {productType && (
              <button
                onClick={() => setProductType(null)}
                className="absolute top-4 left-4 text-white/60 hover:text-white transition-colors z-10 bg-[#132947]/80 rounded-full p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {!productType ? (
              <>
                <h2 className="text-3xl font-bold text-white mb-4 text-center">What Would You Like to Order?</h2>
                <p className="text-white/70 text-center mb-8">Support SPUR with every purchase</p>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Gift Box Option */}
                  <button
                    onClick={() => setProductType('giftBox')}
                    className="bg-gradient-to-br from-[#fda36b]/10 to-transparent border-2 border-[#fda36b]/30 rounded-xl p-8 hover:border-[#fda36b]/60 hover:from-[#fda36b]/20 transition-all group"
                  >
                    <Gift className="w-16 h-16 text-[#fda36b] mx-auto mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-2xl font-bold text-white mb-3">Order Gift Box</h3>
                    <p className="text-white/70 mb-4">Complete gift box with coffee, bamboo scoop, and story card</p>
                    <div className="text-[#fda36b] font-bold text-lg">Starting at $42.50</div>
                  </button>

                  {/* Single Bag Option */}
                  <button
                    onClick={() => setProductType('singleBag')}
                    className="bg-gradient-to-br from-white/5 to-transparent border-2 border-white/20 rounded-xl p-8 hover:border-[#fda36b]/60 hover:from-white/10 transition-all group"
                  >
                    <Coffee className="w-16 h-16 text-[#fda36b] mx-auto mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-2xl font-bold text-white mb-3">Order Bag Only</h3>
                    <p className="text-white/70 mb-4">12oz bag of Header Holiday coffee only</p>
                    <div className="text-[#fda36b] font-bold text-lg">Starting at $21</div>
                  </button>
                </div>
              </>
            ) : productType === 'giftBox' ? (
              <>
                <h2 className="text-3xl font-bold text-white mb-4 text-center">Gift Box Options</h2>
                <p className="text-white/70 text-center mb-6">Choose delivery or shipping</p>
                <div className="grid md:grid-cols-2 gap-6">
                {/* Local Delivery/Pickup */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#fda36b]/50 transition-all group">
                  <div className="text-center mb-4">
                    <MapPin className="w-12 h-12 text-[#fda36b] mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-white mb-2">Local Delivery or Pickup</h3>
                    <p className="text-white/70 text-sm mb-4">Free local delivery or pickup at SPUR</p>
                  </div>
                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-[#fda36b] mb-1">$42.50</div>
                    <div className="text-white/50 text-sm mb-3">per gift box</div>
                    <div className="text-white/60 text-xs text-left space-y-1">
                      <div>(1) 12oz bag of Header Holiday</div>
                      <div>(1) Commemorative Bamboo Spoon</div>
                      <div>(1) Story Card about supporting SPUR</div>
                    </div>
                  </div>
                  <a
                    href="https://buy.stripe.com/dRm6oI7XF3KL1Fz2Js3VC0a"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-[#fda36b] text-[#132947] py-3 rounded-lg font-bold text-center hover:bg-[#fda36b]/90 transition-all"
                  >
                    Order Gift Box - Local
                  </a>
                </div>

                {/* Shipping */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#fda36b]/50 transition-all group">
                  <div className="text-center mb-4">
                    <Truck className="w-12 h-12 text-[#fda36b] mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-white mb-2">Ship Anywhere in the US</h3>
                    <p className="text-white/70 text-sm mb-4">Shipping included</p>
                  </div>
                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-[#fda36b] mb-1">$52.50</div>
                    <div className="text-white/50 text-sm mb-3">per gift box + shipping</div>
                    <div className="text-white/60 text-xs text-left space-y-1">
                      <div>(1) 12oz bag of Header Holiday</div>
                      <div>(1) Commemorative Bamboo Spoon</div>
                      <div>(1) Story Card about supporting SPUR</div>
                    </div>
                  </div>
                  <a
                    href="https://buy.stripe.com/28E28s1zhepp9810Bk3VC0b"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-[#fda36b] text-[#132947] py-3 rounded-lg font-bold text-center hover:bg-[#fda36b]/90 transition-all"
                  >
                    Order Gift Box - Shipped
                  </a>
                </div>
              </div>
              </>
            ) : (
              <>
                <h2 className="text-3xl font-bold text-white mb-4 text-center">Single Bag Options</h2>
                <p className="text-white/70 text-center mb-6">Choose delivery or shipping</p>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Single Bag - Local */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#fda36b]/50 transition-all group">
                  <div className="text-center mb-4">
                    <MapPin className="w-10 h-10 text-[#fda36b] mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-white mb-2">Local Delivery or Pickup</h3>
                    <p className="text-white/70 text-sm mb-4">Free local delivery or pickup at SPUR</p>
                  </div>
                  <div className="text-center mb-6">
                    <div className="text-3xl font-bold text-[#fda36b] mb-1">$21</div>
                    <div className="text-white/50 text-sm mb-3">12oz bag only</div>
                    <div className="text-white/60 text-xs text-left">
                      <div>(1) 12oz bag of Header Holiday coffee</div>
                    </div>
                  </div>
                  <a
                    href="https://buy.stripe.com/eVq7sM91Jbddckd5VE3VC0e"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-white/10 text-white border-2 border-white/20 py-3 rounded-lg font-bold text-center hover:bg-white/20 hover:border-[#fda36b]/50 transition-all"
                  >
                    Order Single Bag - Local
                  </a>
                </div>

                {/* Single Bag - Shipped */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#fda36b]/50 transition-all group">
                  <div className="text-center mb-4">
                    <Truck className="w-10 h-10 text-[#fda36b] mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-white mb-2">Ship Anywhere in the US</h3>
                    <p className="text-white/70 text-sm mb-4">Shipping included</p>
                  </div>
                  <div className="text-center mb-6">
                    <div className="text-3xl font-bold text-[#fda36b] mb-1">$27</div>
                    <div className="text-white/50 text-sm mb-3">12oz bag + shipping</div>
                    <div className="text-white/60 text-xs text-left">
                      <div>(1) 12oz bag of Header Holiday coffee</div>
                    </div>
                  </div>
                  <a
                    href="https://buy.stripe.com/9B69AU1zh811ac55VE3VC0f"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-white/10 text-white border-2 border-white/20 py-3 rounded-lg font-bold text-center hover:bg-white/20 hover:border-[#fda36b]/50 transition-all"
                  >
                    Order Single Bag - Shipped
                  </a>
                </div>
              </div>
              </>
            )}

            <p className="text-white/50 text-sm text-center mt-6">
              Your purchase directly supports SPUR's mission to help families in our community. Questions? Contact us at hello@bondcoffee.com
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
